import React, { useEffect, useState } from 'react';
import { Task, Trade, createTask, markTaskDone, reopenTask, uploadTaskPhoto, getTaskPhotoUrl } from '../api/client';
import { AddTaskModal } from './AddTaskModal';
import { AddPhotoToTaskModal } from './AddPhotoToTaskModal';

interface Props {
  roomId: number;
  tasks: Task[];
  trades: Trade[];
  onTasksChange: () => void;
}

export function TaskList({ roomId, tasks, trades, onTasksChange }: Props) {
  const [tradeFilter, setTradeFilter] = useState<number | 'all'>('all');
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showAddPhotoModal, setShowAddPhotoModal] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [pendingDoneIds, setPendingDoneIds] = useState<Set<number>>(new Set());
  const [showReopenConfirm, setShowReopenConfirm] = useState<number | null>(null);

  const handleAddTask = async (description: string, tradeId: number | null, photo: File | null, dueDate: string | null = null) => {
    const newTask = await createTask(roomId, description, tradeId, null, dueDate);
    if (photo && newTask.id) {
      await uploadTaskPhoto(newTask.id, photo);
    }
    onTasksChange();
  };

  const handleAddPhotoToTask = async (taskId: number, photo: File) => {
    await uploadTaskPhoto(taskId, photo);
    onTasksChange();
  };

  const handleMarkDone = async (taskId: number) => {
    setPendingDoneIds(prev => new Set(prev).add(taskId));
    try {
      await markTaskDone(taskId, '');
      onTasksChange();
    } catch (error) {
      console.error('Failed to mark task done:', error);
      alert('Failed to mark task done. Please try again.');
      setPendingDoneIds(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  };

  const handleReopenTask = async (taskId: number) => {
    try {
      await reopenTask(taskId);
      onTasksChange();
      setShowReopenConfirm(null);
    } catch (error) {
      console.error('Failed to reopen task:', error);
      alert('Failed to reopen task. Please try again.');
    }
  };

  useEffect(() => {
    setPendingDoneIds(prev => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      prev.forEach(id => {
        const task = tasks.find(t => t.id === id);
        if (!task || task.status === 'done') {
          next.delete(id);
        }
      });
      return next.size === prev.size ? prev : next;
    });
  }, [tasks]);

  // Apply trade filter
  const filteredTasks = tradeFilter === 'all'
    ? tasks
    : tasks.filter(t => t.trade_id === tradeFilter);

  const openTasks = filteredTasks.filter(t => t.status === 'open');
  const doneTasks = filteredTasks.filter(t => t.status === 'done');

  const renderTaskRow = (task: Task, isDone: boolean) => {
    const photoUrl = getTaskPhotoUrl(task.photo_url);
    const isPendingDone = pendingDoneIds.has(task.id);
    const isCompleted = isDone || isPendingDone;

    return (
      <div
        key={task.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px',
          borderBottom: '1px solid #eee',
          opacity: isCompleted ? 0.7 : 1,
          minHeight: task.photo_url ? '64px' : 'auto'
        }}
      >
        <button
          type="button"
          onClick={() => {
            if (!isCompleted) {
              handleMarkDone(task.id);
            } else if (isDone) {
              setShowReopenConfirm(task.id);
            }
          }}
          aria-label={isCompleted ? 'Task marked done - click to reopen' : 'Mark task done'}
          style={{
            width: '22px',
            height: '22px',
            marginRight: '12px',
            borderRadius: '4px',
            border: `2px solid ${isCompleted ? '#28a745' : '#999'}`,
            background: isCompleted ? '#28a745' : '#fff',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: 1,
            flexShrink: 0
          }}
        >
          {isCompleted ? '✓' : ''}
        </button>

        {/* Task thumbnail */}
        {photoUrl && (
          <div
            onClick={() => setLightboxImage(photoUrl)}
            style={{
              width: '48px',
              height: '48px',
              marginRight: '12px',
              borderRadius: '4px',
              overflow: 'hidden',
              cursor: 'pointer',
              flexShrink: 0,
              border: '1px solid #ddd'
            }}
          >
            <img
              src={photoUrl}
              alt="Task photo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
        )}

        <span style={{
          flex: 1,
          textDecoration: isCompleted ? 'line-through' : 'none'
        }}>
          {task.description}
        </span>

        {isDone && (
          <span style={{
            fontSize: '11px',
            padding: '2px 8px',
            background: '#e8f5e9',
            color: '#2e7d32',
            borderRadius: '10px',
            marginRight: '8px'
          }}>
            Done
          </span>
        )}

        {isPendingDone && !isDone && (
          <span style={{
            fontSize: '11px',
            padding: '2px 8px',
            background: '#e3f2fd',
            color: '#1565c0',
            borderRadius: '10px',
            marginRight: '8px'
          }}>
            Marking done...
          </span>
        )}

        {(isDone ? task.completed_by : task.created_by) && (
          <span style={{ color: '#666', fontSize: '12px' }}>
            - {isDone ? task.completed_by : task.created_by}
          </span>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <h2 style={{ marginBottom: '16px' }}>Work to Complete</h2>

      {/* Trade filter dropdown */}
      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="trade-filter" style={{ marginRight: '8px', fontSize: '14px' }}>Filter by trade:</label>
        <select
          id="trade-filter"
          value={tradeFilter}
          onChange={(e) => setTradeFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            fontSize: '14px',
            minWidth: '150px',
          }}
        >
          <option value="all">All Trades</option>
          {trades.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
        <button
          onClick={() => setShowAddTaskModal(true)}
          style={{
            padding: '10px 20px',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          + Add Task
        </button>
        <button
          onClick={() => setShowAddPhotoModal(true)}
          style={{
            padding: '10px 20px',
            background: '#28a745',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          + Add Photo
        </button>
      </div>

      {/* Open tasks */}
      {openTasks.map(task => renderTaskRow(task, false))}

      {/* Done tasks */}
      {doneTasks.length > 0 && (
        <>
          <h3 style={{ marginTop: '16px', color: '#666' }}>Done</h3>
          {doneTasks.map(task => renderTaskRow(task, true))}
        </>
      )}

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        onSubmit={handleAddTask}
        trades={trades}
      />

      {/* Add Photo to Task Modal */}
      <AddPhotoToTaskModal
        isOpen={showAddPhotoModal}
        onClose={() => setShowAddPhotoModal(false)}
        onSubmit={handleAddPhotoToTask}
        tasks={tasks}
      />

      {/* Lightbox */}
      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            cursor: 'pointer'
          }}
        >
          <img
            src={lightboxImage}
            alt="Task photo"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain'
            }}
          />
          <button
            onClick={() => setLightboxImage(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            &times;
          </button>
        </div>
      )}

      {/* Reopen Confirmation Dialog */}
      {showReopenConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => setShowReopenConfirm(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '400px',
              width: '90%'
            }}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>Reopen Task?</h3>
            <p style={{ margin: '0 0 24px 0', color: '#666' }}>
              Are you sure you want to mark this task as not done? The task will move back to "Work to Complete".
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowReopenConfirm(null)}
                style={{
                  padding: '8px 16px',
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleReopenTask(showReopenConfirm)}
                style={{
                  padding: '8px 16px',
                  background: '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Reopen Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
